// SVG-rendered natal chart wheel.
// Geometry: two nested circles. Outer ring carries 12 zodiac signs (30° each),
// inner area contains planets positioned by ecliptic longitude and house
// cusps drawn as radial lines. Aspects are drawn as chords inside the inner
// disc, coloured by aspect nature.
//
// Convention: ecliptic longitude 0° = Aries 0° = left side of chart (9 o'clock
// position in SVG — that matches astrological convention where the Ascendant
// would be on the left). For a chart without an Ascendant (solar chart), we
// still anchor 0° Aries at 9 o'clock — sign wheel just doesn't rotate.

import type { NatalChart } from '../api';

interface Props {
  chart: NatalChart;
  size?: number;
}

const SIGN_GLYPHS: string[] = [
  '\u2648', '\u2649', '\u264A', '\u264B', '\u264C', '\u264D',
  '\u264E', '\u264F', '\u2650', '\u2651', '\u2652', '\u2653',
];

const PLANET_GLYPHS: Record<string, string> = {
  sun: '\u2609', moon: '\u263D', mercury: '\u263F', venus: '\u2640',
  mars: '\u2642', jupiter: '\u2643', saturn: '\u2644',
  uranus: '\u2645', neptune: '\u2646', pluto: '\u2647',
};

const ASPECT_COLOURS: Record<string, string> = {
  conjunction: '#b4a8d4',
  sextile: '#7bb58a',
  square: '#d78c7a',
  trine: '#7bb58a',
  opposition: '#d78c7a',
};

// Rotate so ASC is on the left (9 o'clock) when available. If no houses
// (solar chart), 0° Aries lands on the left.
function longitudeToAngle(lon: number, ascLongitude: number | null): number {
  const rot = ascLongitude ?? 0;
  const rel = ((lon - rot) % 360 + 360) % 360;
  // SVG Y axis points down; angle is measured counter-clockwise from the
  // left horizontal (9 o'clock). We map longitude 0 → 180° polar, i.e. (-1, 0).
  return (180 - rel) * (Math.PI / 180);
}

function polar(cx: number, cy: number, r: number, angleRad: number): { x: number; y: number } {
  return { x: cx + r * Math.cos(angleRad), y: cy - r * Math.sin(angleRad) };
}

export function ChartWheel({ chart, size = 360 }: Props) {
  const cx = size / 2;
  const cy = size / 2;
  const outer = size / 2 - 4;
  const zodiacInner = outer - 28;
  const planetRing = zodiacInner - 24;
  const innerDisc = planetRing - 14;

  const asc = chart.houses?.ascendant ?? null;

  // Zodiac ring — 12 sector arcs with glyph in middle.
  const signTicks: Array<{ path: string; key: number }> = [];
  const signGlyphs: Array<{ x: number; y: number; glyph: string; key: number }> = [];
  for (let i = 0; i < 12; i++) {
    const sectorStart = i * 30;
    const sectorEnd = sectorStart + 30;
    const a1 = longitudeToAngle(sectorStart, asc);
    const a2 = longitudeToAngle(sectorEnd, asc);
    const p1 = polar(cx, cy, outer, a1);
    const p2 = polar(cx, cy, outer, a2);
    const q1 = polar(cx, cy, zodiacInner, a2);
    const q2 = polar(cx, cy, zodiacInner, a1);
    const largeArc = 0;
    const sweep = 0;
    const path = `M${p1.x},${p1.y} A${outer},${outer} 0 ${largeArc} ${sweep} ${p2.x},${p2.y} L${q1.x},${q1.y} A${zodiacInner},${zodiacInner} 0 ${largeArc} 1 ${q2.x},${q2.y} Z`;
    signTicks.push({ path, key: i });

    const midAngle = longitudeToAngle(sectorStart + 15, asc);
    const gPos = polar(cx, cy, (outer + zodiacInner) / 2, midAngle);
    signGlyphs.push({ x: gPos.x, y: gPos.y, glyph: SIGN_GLYPHS[i]!, key: i });
  }

  // House cusp radii (only if houses present).
  const houseLines: Array<{ x1: number; y1: number; x2: number; y2: number; key: number; bold: boolean }> = [];
  if (chart.houses) {
    for (let i = 0; i < 12; i++) {
      const cusp = chart.houses.cusps[i]!;
      const a = longitudeToAngle(cusp.longitude, asc);
      const p1 = polar(cx, cy, 6, a);
      const p2 = polar(cx, cy, zodiacInner, a);
      houseLines.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, key: i, bold: i === 0 || i === 9 });
    }
  }

  // Planets at their ecliptic longitudes. Space them radially if two planets
  // are within ~3° so the glyphs don't overlap.
  const planetEntries = Object.entries(chart.planets).map(([id, pos]) => ({ id, longitude: pos.longitude, retrograde: pos.retrograde }));
  planetEntries.sort((a, b) => a.longitude - b.longitude);
  const glyphPositions: Array<{ x: number; y: number; glyph: string; retro: boolean; key: string }> = [];
  let lastLon = -Infinity;
  let offset = 0;
  for (const p of planetEntries) {
    if (Math.abs(p.longitude - lastLon) < 4) {
      offset += 8;
    } else {
      offset = 0;
    }
    const r = planetRing - offset;
    const a = longitudeToAngle(p.longitude, asc);
    const pos = polar(cx, cy, r, a);
    glyphPositions.push({
      x: pos.x,
      y: pos.y,
      glyph: PLANET_GLYPHS[p.id] ?? '?',
      retro: p.retrograde,
      key: p.id,
    });
    lastLon = p.longitude;
  }

  // Aspect chords inside inner disc.
  const aspectLines = chart.aspects.map((asp, i) => {
    const lonA = chart.planets[asp.a]?.longitude;
    const lonB = chart.planets[asp.b]?.longitude;
    if (lonA == null || lonB == null) return null;
    const a1 = longitudeToAngle(lonA, asc);
    const a2 = longitudeToAngle(lonB, asc);
    const p1 = polar(cx, cy, innerDisc, a1);
    const p2 = polar(cx, cy, innerDisc, a2);
    return (
      <line
        key={`aspect-${i}`}
        x1={p1.x}
        y1={p1.y}
        x2={p2.x}
        y2={p2.y}
        stroke={ASPECT_COLOURS[asp.type] ?? '#666'}
        strokeWidth={asp.type === 'conjunction' ? 0 : 1}
        strokeOpacity={0.5}
      />
    );
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Natal chart">
      {/* Zodiac ring background */}
      {signTicks.map(({ path, key }) => (
        <path
          key={`sec-${key}`}
          d={path}
          fill={key % 2 === 0 ? 'rgba(217, 179, 91, 0.08)' : 'rgba(217, 179, 91, 0.02)'}
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={0.5}
        />
      ))}

      {/* Sign glyphs */}
      {signGlyphs.map(({ x, y, glyph, key }) => (
        <text
          key={`sg-${key}`}
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={16}
          fill="#d9b35b"
        >
          {glyph}
        </text>
      ))}

      {/* Inner disc border */}
      <circle cx={cx} cy={cy} r={zodiacInner} fill="none" stroke="rgba(255,255,255,0.15)" />
      <circle cx={cx} cy={cy} r={innerDisc} fill="none" stroke="rgba(255,255,255,0.08)" />

      {/* House cusps */}
      {houseLines.map(({ x1, y1, x2, y2, key, bold }) => (
        <line
          key={`h-${key}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={bold ? '#d9b35b' : 'rgba(255,255,255,0.14)'}
          strokeWidth={bold ? 1.2 : 0.7}
        />
      ))}

      {/* Aspects */}
      {aspectLines}

      {/* Planets */}
      {glyphPositions.map(({ x, y, glyph, retro, key }) => (
        <g key={`p-${key}`}>
          <circle cx={x} cy={y} r={10} fill="#0b0b14" stroke="rgba(255,255,255,0.3)" />
          <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={13} fill="#f1eadb">
            {glyph}
          </text>
          {retro && (
            <text x={x + 10} y={y + 10} fontSize={8} fill="#d78c7a">
              R
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
